import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { API_URL } from '../config';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user, token } = useContext(AuthContext);
  
  // Array of full product objects
  const [cartItems, setCartItems] = useState([]);
  const [isCartLoading, setIsCartLoading] = useState(false);

  // Load cart initially
  useEffect(() => {
    if (user && token) {
      syncAndFetchDbCart();
    } else {
      loadLocalCart();
    }
  }, [user, token]);

  const loadLocalCart = () => {
    const saved = localStorage.getItem('localCart');
    if (saved) {
      try {
        setCartItems(JSON.parse(saved));
      } catch (e) {
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
  };

  const saveLocalCart = (items) => {
    localStorage.setItem('localCart', JSON.stringify(items));
    setCartItems(items);
  };

  const syncAndFetchDbCart = async () => {
    setIsCartLoading(true);
    try {
      // 1. Check for local items that need syncing
      const local = localStorage.getItem('localCart');
      if (local) {
        let localItems = [];
        try { localItems = JSON.parse(local); } catch(e){}
        
        if (localItems.length > 0) {
          const productIds = localItems.map(item => item.id || item.product_id);
          await fetch(`${API_URL}/api/cart/sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ items: productIds })
          });
          // Clear local storage after sync
          localStorage.removeItem('localCart');
        }
      }

      // 2. Fetch merged cart from DB
      const res = await fetch(`${API_URL}/api/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // The DB returns { product_id, name, price, newPrice, image }
        // We'll normalize to { id, name, price, newPrice, image } for frontend consistency
        const normalized = data.map(item => ({
          ...item,
          id: item.product_id
        }));
        setCartItems(normalized);
      }
    } catch (err) {
      console.error("Cart sync/fetch failed", err);
    }
    setIsCartLoading(false);
  };

  const addToCart = async (product) => {
    // Check if already in cart
    if (cartItems.find(item => item.id === product.id)) {
      return; // Already added
    }

    if (user && token) {
      // Optimistic UI update
      setCartItems([...cartItems, product]);
      try {
        await fetch(`${API_URL}/api/cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ productId: product.id })
        });
      } catch (err) {
        console.error("Failed to add to DB cart", err);
        // Could revert state here on failure
      }
    } else {
      // Save locally
      saveLocalCart([...cartItems, product]);
    }
  };

  const removeFromCart = async (productId) => {
    if (user && token) {
      setCartItems(cartItems.filter(item => item.id !== productId));
      try {
        await fetch(`${API_URL}/api/cart/${productId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (err) {
        console.error("Failed to remove from DB cart", err);
      }
    } else {
      saveLocalCart(cartItems.filter(item => item.id !== productId));
    }
  };

  const clearCart = () => {
    setCartItems([]);
    if (!user) {
      localStorage.removeItem('localCart');
    }
    // (If you want a clear cart endpoint on backend, implement it. 
    // Usually people just remove items one by one or clear on successful order)
  };

  return (
    <CartContext.Provider value={{ cartItems, isCartLoading, addToCart, removeFromCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};
